import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, ExternalLink, Download, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Layout } from "@/components/Layout";
import { fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";
import { useState } from "react";
import { saveContactEntry, exportContactToExcel, getContactEntries } from "@/lib/exportToExcel";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [entryCount, setEntryCount] = useState(() => getContactEntries().length);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveContactEntry({
      الاسم: formData.name,
      البريد: formData.email,
      الهاتف: formData.phone,
      الموضوع: formData.subject,
      الرسالة: formData.message,
    });
    setEntryCount((c) => c + 1);
    setSubmitted(true);
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    setTimeout(() => setSubmitted(false), 4000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "الهاتف",
      value: "0917430505 | 0927430505 | 0947430505",
      link: "tel:0917430505",
    },
    {
      icon: Mail,
      title: "البريد الإلكتروني",
      value: "info@cgot.edu.ly",
      link: "mailto:info@cgot.edu.ly",
    },
    {
      icon: MapPin,
      title: "الموقع",
      value: "بنغازي - الفويهات",
      link: null,
    },
    {
      icon: Clock,
      title: "ساعات العمل",
      value: "الأحد-الخميس 8ص-4م",
      link: null,
    },
  ];

  const quickLinks = [
    {
      title: "بوابة الطالب",
      url: "https://xlserver.al-emtiaz.net//XLWeb/Default.aspx?u=ColTechGateLibya",
      description: "الوصول إلى النظام الأكاديمي",
    },
    {
      title: "منصة المودل",
      url: "https://cgot.edu.ly/moodle44",
      description: "منصة التعلم الإلكتروني",
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="relative bg-gradient-to-b from-primary/10 via-primary/5 to-transparent py-24"
        >
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-5xl font-bold text-foreground mb-6">
                اتصل بنا
              </h1>
              <p className="text-xl text-muted-foreground">
                نحن هنا للإجابة على استفساراتكم ومساعدتكم في أي وقت
              </p>
            </div>
          </div>
        </motion.div>

        <div className="container mx-auto px-4 py-16">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
          >
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <motion.div key={index} variants={staggerItem}>
                  <Card className="h-full hover:shadow-lg transition-shadow duration-200">
                    <CardContent className="p-6 text-center">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {info.title}
                      </h3>
                      {info.link ? (
                        <a
                          href={info.link}
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-muted-foreground">{info.value}</p>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <Card>
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-foreground mb-6">
                    أرسل لنا رسالة
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        الاسم الكامل
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="أدخل اسمك الكامل"
                        required
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        البريد الإلكتروني
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="example@email.com"
                        required
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        رقم الهاتف
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+218 XX XXXXXXX"
                        required
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        الموضوع
                      </label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="موضوع الرسالة"
                        required
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        الرسالة
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="اكتب رسالتك هنا..."
                        required
                        rows={6}
                        className="w-full resize-none"
                      />
                    </div>
                    {submitted ? (
                      <div className="flex items-center justify-center gap-3 w-full py-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
                        <CheckCircle className="w-6 h-6" />
                        <span className="font-semibold text-lg">تم إرسال رسالتك بنجاح! سنتواصل معك قريباً</span>
                      </div>
                    ) : (
                      <Button type="submit" size="lg" className="w-full">
                        إرسال الرسالة
                      </Button>
                    )}
                    {entryCount > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                        <p className="text-blue-700 text-sm mb-2">📨 الرسائل المحفوظة: <strong>{entryCount}</strong></p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={exportContactToExcel}
                          className="border-blue-300 text-blue-700 hover:bg-blue-50 gap-2"
                        >
                          <Download className="w-4 h-4" />
                          تحميل الرسائل (Excel)
                        </Button>
                      </div>
                    )}
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="space-y-8"
            >
              <Card>
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-foreground mb-6">
                    روابط سريعة
                  </h2>
                  <div className="space-y-4">
                    {quickLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-4 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors group"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-foreground mb-1">
                              {link.title}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {link.description}
                            </p>
                          </div>
                          <ExternalLink className="w-5 h-5 text-primary group-hover:translate-x-[-4px] transition-transform" />
                        </div>
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-0 overflow-hidden">
                  <div className="aspect-video bg-muted relative">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d108825.23456789!2d20.0!3d32.1!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzLCsDA2JzAwLjAiTiAyMMKwMDAnMDAuMCJF!5e0!3m2!1sen!2sly!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="موقع كلية بوابة التكنولوجيا"
                    />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30, delay: 0.3 }}
            className="bg-gradient-to-r from-primary to-primary/80 rounded-2xl p-12 text-center text-primary-foreground"
          >
            <h2 className="text-3xl font-bold mb-4">هل تحتاج إلى مساعدة؟</h2>
            <p className="text-lg mb-6 opacity-90">
              فريقنا جاهز للإجابة على جميع استفساراتكم
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                size="lg"
                variant="secondary"
                asChild
              >
                <a href="tel:0917430505">
                  <Phone className="w-5 h-5 ml-2" />
                  اتصل بنا الآن
                </a>
              </Button>
              <Button
                size="lg"
                variant="secondary"
                asChild
              >
                <a href="mailto:info@cgot.edu.ly">
                  <Mail className="w-5 h-5 ml-2" />
                  راسلنا عبر البريد
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}